/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { type BaseRequestOptionsArgs, type Filter, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type TaskOptionsArgs, TaskService } from 'app/common/services';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type CreateNfsParams } from '../share';
import { type FileSystem } from './../../../../../common/types/mo.d';

@Injectable()
export class NfsShareService {
    constructor(
        private http: HttpService,
        private taskService: TaskService,
    ) { }

    getBatchRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('NFSShare.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    countRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('NFSShare.count', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    getBatch(fsID: number | string, dtreeID: number | string, vstoreId: string, range: number[], options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(vstoreId)
            .setParams({
                range: `[${range[0]}-${range[1]}]`,
            })
            .setFilters([
                { field: 'FSID', matchMode: '::', value: fsID },
                { field: 'DTREEID', matchMode: '::', value: dtreeID },
            ]);
        return this.http.get(rest, options);
    }

    getCount(fsID: number | string, dtreeID: number | string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.countRest(vstoreId).setFilters([
            { field: 'FSID', matchMode: '::', value: fsID },
            { field: 'DTREEID', matchMode: '::', value: dtreeID },
        ]);
        return this.http.get(rest, options);
    }

    get(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSShare.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options);
    }

    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSShare.post');
        return this.http.post(rest, params, options);
    }

    createByOmtask(objName: string, params: CreateNfsParams, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('nfsShare.post', 'V2');
        return this.taskService.post('createNfsShare', objName, rest, params, options);
    }

    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSShare.put').format(id);
        return this.http.put(rest, params, options);
    }

    delete(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSShare.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }


    getBatchFSRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    getBatchFSCountRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.count');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 批量查询文件系统
     */
    getBatchFs(filters?: Filter[], params?: { [key: string]: any }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.getBatch');
        if (filters) {
            rest.setFilters(filters);
        }

        if (params) {
            rest.setParams(params);
        }

        return this.http.get(rest, options);
    }

    /**
     * 批量查询文件系统count
     */
    getBatchCountFs(filters?: Filter[], params?: { [key: string]: any }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.count');
        if (filters) {
            rest.setFilters(filters);
        }

        if (params) {
            rest.setParams(params);
        }

        return this.http.get(rest, options);
    }

    /**
     * 查询单个dtree
     */
    getDtree(dtreeId: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.get').format(dtreeId);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options);
    }

    getBatchDtreeRest(fileSystem?: FileSystem): Rest {
        const rest = this.http.makeUrl('dtree.getBatch');
        if (fileSystem) {
            rest.setParams({
                PARENTID: fileSystem.ID,
                vstoreId: fileSystem.vstoreId,
            });
        }
        return rest;
    }

    /**
     * 查询NFS服务的设置
     */
    getNFSService(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSService.get');
        return this.http.get(rest, options);
    }

    /**
     * 查询存储池 列表
     */
    getAllPools(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('storagePool.getBatch');
        return this.http.get(rest, options);
    }

    /**
     * 查询对应名称的文件系统
     */
    getFSOnName(params: FileSystem, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.getBatch')
            .setFilters([
                { field: 'NAME', matchMode: '::', value: params.name },
                { field: 'vstoreId', matchMode: '::', value: params.vstoreId },
            ]);
        return this.http.get(rest, options);
    }

    /**
     * 获取对应租户NFS智能推荐数据
     */
    getSmartguiNFS(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartgui.getNfsshare', 'V2').format(vstoreId);
        return this.http.get(rest, options);
    }

    /**
     * 查询单个文件系统
     * @param id
     */
    getFileSystem(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * nfs共享名校验
     */
    nfsShareNameValidator(control: AbstractControl): ValidationErrors | null {
        // 共享名称只能以“/”开头，其后只能包含字母、数字、特殊字符!\\\"#&%$'()*+-,.:;<=>?@[]^_`{|}~和空格，除“/”外取值范围为1~255个字符。
        if (!control.value) {
            return null;
        }
        const reg = /^\/{1}[a-zA-Z0-9_!"#\$%&'\(\)\*\+-\.;<=:,>\?@\[\\\]\^_`\{\|\}\s~]{1,255}$/;

        // 不能有制表符
        const invalidReg = /[\t]+/;
        if (invalidReg.test(control.value)) {
            return { nfsShareName: true };
        }

        const valid = reg.test(control.value);
        return valid ? null : { nfsShareName: true };
    }

}
